import { useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

import { apiRequest, getImageUrl } from "../../../core/common/api";
import AppImage from "../../../shared/AppImage";
import ToggleSwitch from "../../../shared/ToggleSwitch";

const MAX_IMAGES = 10;
const MAX_SIZE = 5 * 1024 * 1024;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

type Category = {
  _id: string;
  name: string;
  isActive?: boolean;
};

type SubCategory = {
  _id: string;
  name: string;
  category: string;
  isActive?: boolean;
};

type ProductForm = {
  name: string;
  isActive: boolean;
  shortDescription: string;
  description: string;
  category: string;
  subCategory: string;
  price: string;
  sellingPrice: string;
  discountEndsAt: string;
  stock: string;
  shippingCharge: string;
  isReturnable: boolean;
  isFeatured: boolean;
  isBestSeller: boolean;
  isNewArrival: boolean;
};

/* datetime-local inputs use the admin's local time without a timezone suffix */
const toDateTimeLocal = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

type ProductImage = {
  id: string;
  file: File;
  preview: string;
};

type VariantRow = {
  _id?: string;
  color: string;
  colorCode: string;
  size: string;
  packSize: string;
  price: string;
  sellingPrice: string;
  stock: string;
};

const MAX_VARIANT_STOCK = 9999;

const emptyVariant = (): VariantRow => ({
  color: "",
  colorCode: "",
  size: "",
  packSize: "",
  price: "",
  sellingPrice: "",
  stock: "0",
});

const variantKey = (row: { color: string; size: string; packSize: string }) =>
  [row.color, row.size, row.packSize].map((value) => value.trim().toLowerCase()).join("|");

const splitList = (value: string) =>
  Array.from(new Set(value.split(",").map((item) => item.trim()).filter(Boolean)));

const initialForm: ProductForm = {
  name: "",
  isActive: true,
  shortDescription: "",
  description: "",
  category: "",
  subCategory: "",
  price: "",
  sellingPrice: "",
  discountEndsAt: "",
  stock: "",
  shippingCharge: "",
  isReturnable: true,
  isFeatured: false,
  isBestSeller: false,
  isNewArrival: false,
};

const ProductAdd = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [form, setForm] = useState<ProductForm>(initialForm);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [categoryId, setCategoryId] = useState("");
  const [images, setImages] = useState<ProductImage[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const imagesRef = useRef<ProductImage[]>([]);
  const [saving, setSaving] = useState(false);
  const [variants, setVariants] = useState<VariantRow[]>([]);
  const [quickColors, setQuickColors] = useState("");
  const [quickSizes, setQuickSizes] = useState("");
  const [quickPackSizes, setQuickPackSizes] = useState("");

  const variantStockTotal = useMemo(
    () => variants.reduce((total, row) => total + (Number(row.stock) || 0), 0),
    [variants],
  );

  const discountPercent = useMemo(() => {
    const price = Number(form.price);
    const selling = Number(form.sellingPrice);

    return price > 0 && selling > 0 && selling < price
      ? Math.round(((price - selling) / price) * 100)
      : 0;
  }, [form.price, form.sellingPrice]);

  const hasDiscount =
    discountPercent > 0 || variants.some((row) => Number(row.sellingPrice || 0) > 0);

  const categorySubCategories = useMemo(
    () =>
      subCategories.filter(
        (item) =>
          String(item.category) === categoryId && item.isActive !== false,
      ),
    [subCategories, categoryId],
  );

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const meta = await apiRequest("/products/form-meta", "GET");

        setCategories(
          (meta.categories || []).filter(
            (item: Category) => item.isActive !== false,
          ),
        );
        setSubCategories(meta.subCategories || []);

        if (!isEditMode && meta.defaultShippingCharge != null) {
          setForm((prev) => ({
            ...prev,
            shippingCharge: String(meta.defaultShippingCharge),
          }));
        }
      } catch (error: any) {
        Swal.fire("Error", error.message, "error");
      }
    };

    loadInitialData();
  }, [isEditMode]);

  useEffect(() => {
    if (categoryId || !form.category || !categories.length) return;

    const matchingCategory = categories.find(
      (category) =>
        category.name.trim().toLowerCase() ===
        form.category.trim().toLowerCase(),
    );

    if (matchingCategory) {
      setCategoryId(matchingCategory._id);
    }
  }, [categories, categoryId, form.category]);

  useEffect(() => {
    if (!id) return;

    const fetchProductToEdit = async () => {
      try {
        const res = await apiRequest(`/products/${id}`, "GET");
        if (res.success && res.product) {
          const prod = res.product;
          setForm({
            name: prod.name || "",
            isActive: prod.isActive,
            shortDescription: prod.shortDescription || "",
            description: prod.description || "",
            category: prod.category || "",
            subCategory: prod.subCategory || "",
            price: String(prod.price || ""),
            sellingPrice: String(prod.sellingPrice || ""),
            discountEndsAt: toDateTimeLocal(prod.discountEndsAt),
            stock: String(prod.stock ?? ""),
            shippingCharge: String(prod.shippingCharge || ""),
            isReturnable: Boolean(prod.isReturnable),
            isFeatured: Boolean(prod.isFeatured),
            isBestSeller: Boolean(prod.isBestSeller),
            isNewArrival: Boolean(prod.isNewArrival),
          });

          setVariants(
            Array.isArray(prod.variants)
              ? prod.variants.map((variant: any) => ({
                  _id: variant._id,
                  color: variant.color || "",
                  colorCode: variant.colorCode || "",
                  size: variant.size || "",
                  packSize: variant.packSize || "",
                  price: variant.price ? String(variant.price) : "",
                  sellingPrice: variant.sellingPrice ? String(variant.sellingPrice) : "",
                  stock: String(variant.stock ?? 0),
                }))
              : [],
          );

          setExistingImages(
            Array.isArray(prod.images)
              ? prod.images.filter(
                  (image: unknown): image is string =>
                    typeof image === "string" && Boolean(image),
                )
              : [],
          );
        }
      } catch (error: any) {
        Swal.fire("Error", error.message || "Failed to load product", "error");
      }
    };

    fetchProductToEdit();
  }, [id]);

  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  useEffect(
    () => () => {
      imagesRef.current.forEach((image) => URL.revokeObjectURL(image.preview));
    },
    [],
  );

  const update = <K extends keyof ProductForm>(
    key: K,
    value: ProductForm[K],
  ) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const changeCategory = (id: string) => {
    const category = categories.find((item) => item._id === id);

    setCategoryId(id);

    setForm((current) => ({
      ...current,
      category: category?.name || "",
      subCategory: "",
    }));
  };

  const selectImages = (files: FileList | null) => {
    if (!files?.length) return;

    const available = MAX_IMAGES - images.length - existingImages.length;
    const selected = Array.from(files).slice(0, available);

    const validImages: ProductImage[] = [];

    selected.forEach((file) => {
      if (!IMAGE_TYPES.includes(file.type)) {
        Swal.fire("Error", `${file.name}: Invalid image type`, "error");
        return;
      }

      if (file.size > MAX_SIZE) {
        Swal.fire("Error", `${file.name}: Maximum size is 5MB`, "error");
        return;
      }

      const id = `${file.name}-${file.size}-${file.lastModified}`;

      if (images.some((image) => image.id === id)) return;

      validImages.push({
        id,
        file,
        preview: URL.createObjectURL(file),
      });
    });

    setImages((current) => [...current, ...validImages].slice(0, MAX_IMAGES));
  };

  const removeImage = (id: string) => {
    setImages((current) => {
      const image = current.find((item) => item.id === id);

      if (image) URL.revokeObjectURL(image.preview);

      return current.filter((item) => item.id !== id);
    });
  };

  const removeExistingImage = (url: string) => {
    setExistingImages((current) => current.filter((image) => image !== url));
  };

  const addVariant = () => {
    setVariants((current) => [...current, emptyVariant()]);
  };

  const changeVariant = (index: number, field: keyof VariantRow, value: string) => {
    setVariants((current) =>
      current.map((row, position) => {
        if (position !== index) return row;
        const next = { ...row, [field]: value };
        if (field === "price" && !Number(value)) next.sellingPrice = "";
        return next;
      }),
    );
  };

  const removeVariant = (index: number) => {
    setVariants((current) => current.filter((_, position) => position !== index));
  };

  const generateVariants = () => {
    const colors = splitList(quickColors);
    const sizes = splitList(quickSizes);
    const packSizes = splitList(quickPackSizes);

    if (!colors.length && !sizes.length && !packSizes.length) {
      Swal.fire(
        "Nothing to add",
        "Enter colours, sizes or pack sizes separated by commas",
        "info",
      );
      return;
    }

    const combos = (colors.length ? colors : [""]).flatMap((color) =>
      (sizes.length ? sizes : [""]).flatMap((size) =>
        (packSizes.length ? packSizes : [""]).map((packSize) => ({ color, size, packSize })),
      ),
    );

    setVariants((current) => {
      const existing = new Set(current.map(variantKey));
      const additions = combos
        .filter((combo) => !existing.has(variantKey(combo)))
        .map((combo) => ({ ...emptyVariant(), ...combo }));
      return [...current, ...additions];
    });
    setQuickColors("");
    setQuickSizes("");
    setQuickPackSizes("");
  };

  const validate = () => {
    const price = Number(form.price);
    const selling = Number(form.sellingPrice || 0);
    const stock = Number(form.stock);

    if (!form.category) return "Category is required";

    if (!Number.isFinite(price) || price < 0) {
      return "Enter a valid price";
    }

    if (selling < 0 || selling > price) {
      return `Selling price (₹${selling}) cannot be more than MRP (₹${price}). MRP is the higher, strikethrough price.`;
    }

    if (
      form.discountEndsAt &&
      hasDiscount &&
      new Date(form.discountEndsAt).getTime() <= Date.now()
    ) {
      return "Discount end time must be in the future";
    }

    if (variants.length) {
      const seen = new Set<string>();
      for (const [index, row] of variants.entries()) {
        const position = `Variant ${index + 1}`;
        const rowStock = Number(row.stock);
        const rowPrice = Number(row.price || 0);
        const rowSelling = Number(row.sellingPrice || 0);

        if (!row.color.trim() && !row.size.trim() && !row.packSize.trim()) {
          return `${position}: enter a colour, size or pack size`;
        }
        if (!Number.isInteger(rowStock) || rowStock < 0 || rowStock > MAX_VARIANT_STOCK) {
          return `${position}: stock must be between 0 and ${MAX_VARIANT_STOCK}`;
        }
        if (!Number.isFinite(rowPrice) || rowPrice < 0 || !Number.isFinite(rowSelling) || rowSelling < 0) {
          return `${position}: enter a valid price`;
        }
        if (rowSelling > 0 && !rowPrice) return `${position}: enter a price before adding a selling price`;
        if (rowSelling > rowPrice) {
          return `${position}: selling price (₹${rowSelling}) cannot be more than MRP (₹${rowPrice}). MRP is the higher, strikethrough price.`;
        }
        if (seen.has(variantKey(row))) return `${position}: this variant is added twice`;
        seen.add(variantKey(row));
      }
    } else if (!Number.isInteger(stock) || stock < 0 || stock > 9999) {
      return "Stock must be between 0 and 9999";
    }

    return "";
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const error = validate();

    if (error) {
      Swal.fire("Validation Error", error, "error");
      return;
    }

    setSaving(true);

    try {
      const payload = new FormData();

      Object.entries(form).forEach(([key, value]) => {
        if (key === "discountEndsAt") {
          payload.append(
            key,
            value && hasDiscount ? new Date(String(value)).toISOString() : "",
          );
          return;
        }
        payload.append(key, String(value));
      });

      payload.append(
        "variants",
        JSON.stringify(
          variants.map((row) => ({
            ...(row._id ? { _id: row._id } : {}),
            color: row.color.trim(),
            colorCode: row.color.trim() ? row.colorCode : "",
            size: row.size.trim(),
            packSize: row.packSize.trim(),
            price: Number(row.price) || 0,
            sellingPrice: Number(row.sellingPrice) || 0,
            stock: Number(row.stock) || 0,
          })),
        ),
      );

      if (isEditMode) {
        payload.append("keepImages", JSON.stringify(existingImages));
      }

      images.forEach(({ file }) => {
        payload.append("images", file);
      });

      const endpoint = isEditMode ? `/products/${id}` : "/products";
      const method = isEditMode ? "PUT" : "POST";

      const result = await apiRequest(endpoint, method, payload);

      if (!result.success) {
        throw new Error(
          result.message ||
            `Unable to ${isEditMode ? "update" : "create"} product`,
        );
      }

      await Swal.fire({
        title: isEditMode ? "Product Updated" : "Product Published",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      navigate("/products");
    } catch (error: any) {
      Swal.fire(
        isEditMode ? "Update Failed" : "Add Product Failed",
        error.message,
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="product-add-form" onSubmit={handleSubmit}>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">MANAGEMENT</div>

          <h1 className="page-title">
            Product <em>{isEditMode ? "Edit" : "Add"}</em>
          </h1>

          <div className="page-sub">
            {isEditMode
              ? "Update product details and inventory."
              : "Create and publish a new product."}
          </div>
        </div>

        <div className="page-hdr-actions">
          <button
            type="submit"
            className="btn-sm btn-primary"
            disabled={saving}
          >
            {saving
              ? isEditMode
                ? "Updating..."
                : "Publishing..."
              : isEditMode
                ? "Update Product"
                : "Publish Product"}
          </button>
        </div>
      </div>

      <section className="add-grid">
        <section className="add-card">
          <h2>Product Information</h2>

          <div className="add-form-grid">
            <Field label="Product Name" required className="span-2">
              <input
                required
                maxLength={100}
                value={form.name}
                onChange={(event) => update("name", event.target.value)}
                placeholder="Enter product name"
              />
            </Field>

            <Field label="Category" required>
              <select
                required
                value={categoryId}
                onChange={(event) => changeCategory(event.target.value)}
              >
                <option value="">Select category</option>

                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Sub Category">
              <select
                value={form.subCategory}
                disabled={!categoryId}
                onChange={(event) => update("subCategory", event.target.value)}
              >
                <option value="">No subcategory</option>
                {categorySubCategories.map((item) => (
                  <option key={item._id} value={item.name}>
                    {item.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Short Description" className="span-3">
              <input
                maxLength={180}
                value={form.shortDescription}
                onChange={(event) =>
                  update("shortDescription", event.target.value)
                }
                placeholder="Small product highlight"
              />
            </Field>

            <Field label="Description" required className="span-3">
              <div className="editor-shell">
                <textarea
                  required
                  value={form.description}
                  onChange={(event) =>
                    update("description", event.target.value)
                  }
                  placeholder="Write product description..."
                />

                <small>
                  {form.description.split(/\s+/).filter(Boolean).length} words
                </small>
              </div>
            </Field>

            <Field label="MRP (Price)" required>
              <input
                required
                type="number"
                min="0"
                max="99999999"
                step="0.01"
                value={form.price}
                onChange={(event) => update("price", event.target.value)}
                placeholder="0.00"
              />
            </Field>

            <Field label="Selling Price">
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.sellingPrice}
                onChange={(event) => update("sellingPrice", event.target.value)}
                placeholder="0.00"
              />
            </Field>

            <Field label="Discount Percent">
              <input value={`${discountPercent}%`} readOnly />
            </Field>

            <Field label="Discount End Time">
              <input
                type="datetime-local"
                value={form.discountEndsAt}
                disabled={!hasDiscount}
                min={toDateTimeLocal(new Date().toISOString())}
                onChange={(event) => update("discountEndsAt", event.target.value)}
              />
              <small>
                {hasDiscount
                  ? "Leave empty for no end. After this time the product and its variants return to full price."
                  : "Set a selling price to add a discount end time."}
              </small>
            </Field>

            <Field label="Stock Quantity" required={!variants.length}>
              {variants.length ? (
                <>
                  <input value={variantStockTotal} readOnly />
                  <small>Auto total of all variant stock.</small>
                </>
              ) : (
                <input
                  required
                  type="number"
                  min="0"
                  max="9999"
                  value={form.stock}
                  onChange={(event) => update("stock", event.target.value)}
                  placeholder="0"
                />
              )}
            </Field>

            <Field label="Shipping Charge">
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.shippingCharge}
                onChange={(event) =>
                  update("shippingCharge", event.target.value)
                }
                placeholder="0.00"
              />
            </Field>

             <Field label="Status">
             <ToggleSwitch
              label="Status"
              checked={form.isActive}
              onChange={(value) => update("isActive", value)}
            /> 
            </Field>



            <ToggleSwitch
              label="Featured Product"
              checked={form.isFeatured}
              onChange={(value) => update("isFeatured", value)}
            />

            <ToggleSwitch
              label="Best Seller"
              checked={form.isBestSeller}
              onChange={(value) => update("isBestSeller", value)}
            />

            <ToggleSwitch
              label="New Arrival"
              checked={form.isNewArrival}
              onChange={(value) => update("isNewArrival", value)}
            />

            <ToggleSwitch
              label="Returnable Product"
              checked={form.isReturnable}
              onChange={(value) => update("isReturnable", value)}
            />
          </div>
        </section>

        <section className="add-card variant-card">
          <div className="variant-card-head">
            <div>
              <h2>Product Variants</h2>
              <p className="variant-card-sub">
                Sell this product in different colours, sizes or pack sizes (e.g. Red / M, 500 ml,
                1 kg). Each variant has its own stock and can have its own price. Skip this section
                if the product has only one type.
              </p>
            </div>
            {variants.length > 0 && (
              <span className="variant-count">
                {variants.length} {variants.length === 1 ? "variant" : "variants"} · {variantStockTotal} in stock
              </span>
            )}
          </div>

          <div className="variant-generator">
            <div className="variant-generator-title">
              <strong>Quick Add</strong>
              <span>Type values separated by commas, then click Add Variants.</span>
            </div>
            <div className="variant-generator-grid">
              <Field label="Colour">
                <input
                  value={quickColors}
                  onChange={(event) => setQuickColors(event.target.value)}
                  placeholder="e.g. Red, Blue, Black"
                />
              </Field>
              <Field label="Size">
                <input
                  value={quickSizes}
                  onChange={(event) => setQuickSizes(event.target.value)}
                  placeholder="e.g. S, M, L, XL"
                />
              </Field>
              <Field label="Pack Size (Quantity / Weight)">
                <input
                  value={quickPackSizes}
                  onChange={(event) => setQuickPackSizes(event.target.value)}
                  placeholder="e.g. 250 ml, 500 ml, 1 kg"
                />
              </Field>
              <button
                type="button"
                className="btn-sm btn-primary variant-generate-btn"
                onClick={generateVariants}
              >
                + Add Variants
              </button>
            </div>
          </div>

          {variants.length === 0 ? (
            <div className="variant-empty">
              <strong>No variants added</strong>
              <span>Use Quick Add above, or add a variant manually.</span>
            </div>
          ) : (
            <div className="table-scroll variant-table-wrap">
              <table className="variant-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Colour</th>
                    <th>Size</th>
                    <th>Pack Size</th>
                    <th>MRP (₹)</th>
                    <th>Selling Price (₹)</th>
                    <th>Stock</th>
                    <th aria-label="Remove" />
                  </tr>
                </thead>
                <tbody>
                  {variants.map((row, index) => {
                    const sellingTooHigh =
                      Number(row.sellingPrice || 0) > 0 &&
                      Number(row.sellingPrice) > Number(row.price || 0);

                    return (
                    <tr key={row._id || index}>
                      <td className="variant-index">{index + 1}</td>
                      <td>
                        <div className="variant-color-cell">
                          <input
                            type="color"
                            className="variant-swatch"
                            title={row.color.trim() ? "Pick colour shown in the app" : "Enter a colour name first"}
                            value={row.colorCode || "#000000"}
                            disabled={!row.color.trim()}
                            onChange={(event) =>
                              changeVariant(index, "colorCode", event.target.value.toUpperCase())
                            }
                          />
                          <input
                            value={row.color}
                            maxLength={50}
                            onChange={(event) => changeVariant(index, "color", event.target.value)}
                            placeholder="Colour"
                          />
                        </div>
                      </td>
                      <td>
                        <input
                          value={row.size}
                          maxLength={30}
                          onChange={(event) => changeVariant(index, "size", event.target.value)}
                          placeholder="Size"
                        />
                      </td>
                      <td>
                        <input
                          value={row.packSize}
                          maxLength={30}
                          onChange={(event) => changeVariant(index, "packSize", event.target.value)}
                          placeholder="e.g. 500 ml"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.price}
                          onChange={(event) => changeVariant(index, "price", event.target.value)}
                          placeholder={form.price ? `₹${form.price} (same)` : "Same as product"}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.sellingPrice}
                          disabled={!Number(row.price)}
                          className={sellingTooHigh ? "is-invalid" : undefined}
                          onChange={(event) => changeVariant(index, "sellingPrice", event.target.value)}
                          placeholder={Number(row.price) ? "Optional" : "Set MRP first"}
                        />
                        {sellingTooHigh && (
                          <small className="variant-error">Must be ≤ MRP ₹{row.price}</small>
                        )}
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          max={MAX_VARIANT_STOCK}
                          value={row.stock}
                          onChange={(event) => changeVariant(index, "stock", event.target.value)}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="variant-remove"
                          title="Remove variant"
                          aria-label="Remove variant"
                          onClick={() => removeVariant(index)}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="variant-footer">
            <button type="button" className="btn-sm btn-outline" onClick={addVariant}>
              + Add Variant Manually
            </button>
            <small>
              MRP is the higher (strikethrough) price; Selling Price is what the customer pays. Leave
              MRP empty to use the product price.
            </small>
          </div>
        </section>

        <section className="add-card">
          <h2>Product Images</h2>

          <div className="image-upload-grid">
            <label className="dropzone">
              <input
                type="file"
                multiple
                accept="image/png,image/jpeg,image/webp"
                onChange={(event) => {
                  selectImages(event.target.files);
                  event.target.value = "";
                }}
              />

              <strong>☁</strong>
              <span>Click to upload images</span>

              <small>
                JPG, PNG, WEBP up to 5MB.{" "}
                {images.length || existingImages.length}/{MAX_IMAGES}
              </small>
            </label>

            <div className="image-gallery">
              {existingImages.map((image, index) => (
                <div key={image}>
                  <AppImage
                    src={getImageUrl(image)}
                    alt={`${form.name || "Product"} ${index + 1}`}
                    crossOrigin="anonymous"
                  />

                  <button
                    type="button"
                    onClick={() => removeExistingImage(image)}
                  >
                    ×
                  </button>

                  {index === 0 && !images.length && <span>Cover</span>}
                </div>
              ))}

              {images.map((image, index) => (
                <div key={image.id}>
                  <img src={image.preview} alt={image.file.name} />

                  <button type="button" onClick={() => removeImage(image.id)}>
                    ×
                  </button>

                  {index === 0 && !existingImages.length && <span>Cover</span>}
                </div>
              ))}
            </div>
          </div>
        </section>
      </section>
    </form>
  );
};

const Field = ({
  label,
  required,
  className = "",
  children,
}: {
  label: string;
  required?: boolean;
  className?: string;
  children: ReactNode;
}) => (
  <label className={`add-field ${className}`}>
    <span>
      {label} {required && <b>*</b>}
    </span>

    {children}
  </label>
);

export default ProductAdd;
